// src/components/crm/JobPunchCorrectionModal.js
//
// jobtimefix-4a9e2d73-b — SEAM B: the operator's door onto the correctJobPunch callable.
//
// WHY THIS IS ITS OWN FILE AND NOT A REUSE OF ShiftEditorForm:
// ShiftEditorForm (src/views/TimesheetsView.js) is a file-local const with no export, and all
// of its substance lives in saveShift inside DayRow, hard-wired to users/{uid}/shifts. Beyond
// that, two hard blockers: its inputs are <input type="time"> — HH:mm with no date, which
// cannot express a punch that crosses midnight, and the fixture this seam exists to fix spans
// 20 hours — and it has no free-text note field (only its sibling ShiftDeleteForm does, and
// that one CONCATENATES note into reason, which the callable's separate reason/note fields are
// better than). So this borrows the visual grammar — title, context line, two time fields with
// a live hours readout beside them, reason select, Cancel/Save — and is written fresh. A shared
// presentational form is a later cleanup, once a third time surface exists.
//
// WHAT THIS COMPONENT DOES NOT DO: it does not decide who may correct time. The callable's
// requireApproveJobTime does, by re-reading the caller's user doc server-side. The gate in
// JobFormModal only decides whether the control is offered.

import React, { useState, useMemo } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { toJsDate } from '../../utils/formatters';

// jobtimefix-4a9e2d73-b — CLIENT MIRROR of JOB_TIME_REASONS in functions/index.js (~line 22988).
// The server is the validator; this list only draws the dropdown. A drifted list here becomes an
// invalid-argument the operator cannot act on, so if the server list changes, change this too.
const JOB_TIME_REASONS = {
    edit: [
        'Correcting clock-in time',
        'Correcting clock-out time',
        'Forgot to clock out',
        'Left running overnight',
        'System error',
        'Other',
    ],
    add: [
        'Forgot to clock in',
        'Employee did not clock in',
        'Worked on this job without a timer',
        'System error',
        'Other',
    ],
    void: [
        'Duplicate punch',
        'Accidental / test punch',
        'Wrong job',
        'Wrong employee',
        'Recorded in error',
        'Other',
    ],
};
const JOB_TIME_NOTE_MAX = 500;                       // functions/index.js JOB_TIME_NOTE_MAX
const PUNCH_BELIEVABLE_MS = 13 * 60 * 60 * 1000;     // functions/index.js PUNCH_BELIEVABLE_MS

const MODE_COPY = {
    edit:  { title: 'Correct this punch',   verb: 'Save correction', reasonLabel: 'Reason for correction' },
    add:   { title: 'Add a time entry',     verb: 'Add time entry',  reasonLabel: 'Reason for manual entry' },
    void:  { title: 'Void this punch',      verb: 'Void punch',      reasonLabel: 'Reason for voiding' },
};

// <input type="datetime-local"> wants 'YYYY-MM-DDTHH:mm' in LOCAL time. toISOString() is UTC and
// would shift the displayed clock time, so build the local string by hand.
const toLocalInputValue = (date) => {
    if (!date || isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const JobPunchCorrectionModal = ({
    isOpen,
    op,                     // 'edit' | 'add' | 'void'
    orgId,
    jobId,                  // ALWAYS occurrenceJobId — never jobData.id (recurkey-7b2f9d34)
    punch,                  // the target row (edit/void); null for add
    punchIndex,             // index into the job's real timePunches array; null for add
    employees,
    billedThroughHours,     // hours of THIS person already billed on an invoice for this job
    otherClosedHours,       // this person's closed punch hours EXCLUDING the target row
    currentUserProfile,     // s9harden-3c7e1a59 — the signed-in editor (own-time check)
    onDone,                 // async () => re-read the job
    onCancel,
}) => {
    const copy = MODE_COPY[op] || MODE_COPY.edit;
    const reasonOptions = JOB_TIME_REASONS[op] || JOB_TIME_REASONS.edit;

    const [inLocal, setInLocal]   = useState(() => toLocalInputValue(toJsDate(punch?.punchIn)));
    const [outLocal, setOutLocal] = useState(() => toLocalInputValue(toJsDate(punch?.punchOut)));
    const [userId, setUserId]     = useState('');
    const [reason, setReason]     = useState(reasonOptions[0]);
    const [note, setNote]         = useState('');
    const [saving, setSaving]     = useState(false);
    const [error, setError]       = useState(null);

    const inDate  = inLocal ? new Date(inLocal) : null;
    const outDate = outLocal ? new Date(outLocal) : null;

    const computedHours = useMemo(() => {
        if (op === 'void') return 0;
        if (!inDate || !outDate || isNaN(inDate.getTime()) || isNaN(outDate.getTime())) return null;
        const ms = outDate.getTime() - inDate.getTime();
        if (ms <= 0) return null;
        return parseFloat((ms / 3600000).toFixed(4));
    }, [op, inLocal, outLocal]); // eslint-disable-line react-hooks/exhaustive-deps

    const hoursLabel = computedHours === null ? '—' : `${computedHours.toFixed(2)}h`;

    // jobtimefix-4a9e2d73-b — CLIENT MIRROR of validateJobPunchWindow / parseJobTimeInstant.
    // The sentences are copied VERBATIM from functions/index.js so the client and the server can
    // never tell the operator two different stories about the same input. The server still
    // validates; this only stops a doomed round trip.
    const validationError = useMemo(() => {
        if (op === 'add' && !userId) return 'Choose who this time belongs to.';
        // s9harden-3c7e1a59 — mirror of the server's own-time rule (founder decision 2026-10-04):
        // a Manager can't correct their own time. Same sentence as correctJobPunch.
        const affectedUid = op === 'add' ? userId : punch?.userId;
        if (currentUserProfile?.role === 'Manager' && affectedUid && affectedUid === currentUserProfile?.id) {
            return "You can't correct your own time. Ask an Owner or Admin to correct it.";
        }
        if (op === 'void') return null;
        if (!inLocal) return 'punchIn is required.';
        if (!outLocal) return 'punchOut is required.';
        if (!inDate || isNaN(inDate.getTime())) return 'punchIn is not a valid timestamp.';
        if (!outDate || isNaN(outDate.getTime())) return 'punchOut is not a valid timestamp.';
        const now = Date.now();
        if (inDate.getTime() > now) return 'Clock-in cannot be in the future.';
        if (outDate.getTime() > now) return 'Clock-out cannot be in the future.';
        if (outDate.getTime() <= inDate.getTime()) return 'Clock-out must be after clock-in.';
        if (outDate.getTime() - inDate.getTime() > PUNCH_BELIEVABLE_MS) {
            return 'That is longer than 13 hours on one job — check the date on both times. A genuinely longer stretch should be entered as two separate punches.';
        }
        return null;
    }, [op, userId, inLocal, outLocal, punch, currentUserProfile]); // eslint-disable-line react-hooks/exhaustive-deps

    // The already-billed warning. Only edit and void can take a total DOWN; add only ever raises
    // it, so it can never strand billed hours. Mirrors sumClockedHours' 2dp rounding.
    const billedThrough = Number(billedThroughHours) || 0;
    const projectedTotal = (op === 'add' || (op === 'edit' && computedHours === null))
        ? null
        : parseFloat(((Number(otherClosedHours) || 0) + (op === 'void' ? 0 : computedHours)).toFixed(2));
    const strandedHours = (projectedTotal !== null && billedThrough > 0 && projectedTotal < billedThrough)
        ? parseFloat((billedThrough - projectedTotal).toFixed(2))
        : 0;

    const targetName = punch?.userName
        || employees?.find(u => u.id === punch?.userId)?.name
        || 'this employee';

    const handleSave = async () => {
        if (validationError) { setError(validationError); return; }
        setSaving(true);
        setError(null);
        try {
            const correct = httpsCallable(functions, 'correctJobPunch');
            await correct({
                orgId,
                jobId,
                op,
                ...(op === 'add'
                    ? { userId }
                    : { punchIndex, expectedPunchIn: toJsDate(punch?.punchIn)?.toISOString() }),
                ...(op === 'void'
                    ? {}
                    : { punchIn: inDate.toISOString(), punchOut: outDate.toISOString() }),
                reason,
                ...(note.trim() ? { note: note.trim() } : {}),
            });
            await onDone();
        } catch (err) {
            // The callable's HttpsError messages were written to be READ by an operator — "This
            // punch changed since the screen loaded — reopen the job and try again.", "That timer
            // is still running. Stop it first, then correct it.", "Only an Admin, Manager or Owner
            // can correct job time." Show them verbatim and leave the modal open. A generic
            // "Something went wrong" would throw away the only thing that says what happened.
            setError(err?.message || 'The correction could not be saved.');
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-full overflow-y-auto">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <h3 className="text-base font-semibold text-gray-800">{copy.title}</h3>
                    <button type="button" onClick={onCancel} disabled={saving} className="text-gray-400 hover:text-gray-600 disabled:opacity-40">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-4 py-3 space-y-3">
                    {op !== 'add' && (
                        <p className="text-xs text-gray-500">
                            {targetName}
                            {toJsDate(punch?.punchIn) && ` — ${toJsDate(punch.punchIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                        </p>
                    )}

                    {op === 'add' && (
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">Employee</label>
                            <select
                                value={userId}
                                onChange={e => setUserId(e.target.value)}
                                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm bg-white text-gray-700"
                            >
                                <option value="">Choose an employee…</option>
                                {(employees || []).map(u => (
                                    <option key={u.id} value={u.id}>{u.name || u.email || u.id}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {op === 'void' ? (
                        <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                            The times stay on the record; the hours go to 0. Voiding keeps the history
                            visible — nothing is deleted.
                        </p>
                    ) : (
                        <div className="flex flex-wrap items-end gap-3">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Clock in</label>
                                <input
                                    type="datetime-local"
                                    value={inLocal}
                                    onChange={e => setInLocal(e.target.value)}
                                    className="border border-gray-200 rounded px-2 py-1.5 text-sm text-gray-700 focus:ring-2 focus:ring-indigo-300 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Clock out</label>
                                <input
                                    type="datetime-local"
                                    value={outLocal}
                                    onChange={e => setOutLocal(e.target.value)}
                                    className="border border-gray-200 rounded px-2 py-1.5 text-sm text-gray-700 focus:ring-2 focus:ring-indigo-300 outline-none"
                                />
                            </div>
                            <span className="text-xs text-gray-400 pb-1.5">{hoursLabel}</span>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{copy.reasonLabel}</label>
                        <select
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm bg-white text-gray-700"
                        >
                            {reasonOptions.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs text-gray-500 mb-1">Note (optional)</label>
                        <textarea
                            value={note}
                            onChange={e => setNote(e.target.value.slice(0, JOB_TIME_NOTE_MAX))}
                            rows={2}
                            placeholder="e.g. tech confirmed he left site at 5pm"
                            className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm text-gray-700 focus:ring-2 focus:ring-indigo-300 outline-none"
                        />
                        <p className="text-[11px] text-gray-400 mt-0.5 text-right">{note.length}/{JOB_TIME_NOTE_MAX}</p>
                    </div>

                    {/* jobtimefix-4a9e2d73-b — ALREADY BILLED. The UI half of the deferred
                        over-billing row: CrmApp's labor bridge bills only the POSITIVE delta
                        (`if (delta <= 0) return;`), so a reduction is silently skipped and the
                        customer stays billed. Warn clearly, save anyway — the credit note is a
                        human decision. */}
                    {strandedHours > 0 && (
                        <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                            <AlertTriangle size={15} className="text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-800">
                                <span className="font-semibold">Already billed.</span>{' '}
                                {billedThrough.toFixed(2)}h of {targetName}'s time on this job is already
                                billed on an invoice. This {op === 'void' ? 'void' : 'correction'} takes
                                their total to {projectedTotal.toFixed(2)}h, so the customer stays billed
                                for {strandedHours.toFixed(2)}h they no longer have. Spatchy does not
                                credit that back automatically — save this, then decide whether the
                                customer gets a credit note.
                            </p>
                        </div>
                    )}

                    {error && (
                        <div className="flex gap-2 bg-red-50 border border-red-200 rounded px-3 py-2">
                            <AlertTriangle size={15} className="text-red-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-red-800">{error}</p>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50">
                    <button type="button" onClick={onCancel} disabled={saving} className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-40">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || !!validationError}
                        title={validationError || undefined}
                        className={`px-3 py-1.5 text-sm text-white rounded flex items-center gap-1.5 disabled:bg-gray-400 ${op === 'void' ? 'bg-gray-700 hover:bg-gray-800' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {saving && <Loader2 size={14} className="animate-spin" />}
                        {saving ? 'Saving…' : copy.verb}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default JobPunchCorrectionModal;
